import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SimpleSelect } from "@/components/ui/simple-select";
import { Briefcase, Loader2, Plus, Trash2, Pencil, X, Receipt } from "lucide-react";
import { toast } from "sonner";
import { ProjectCostsDialog } from "@/components/projects/ProjectCostsDialog";

const STATUS_LABELS = {
  planned: { label: "Geplant", className: "bg-slate-100 text-slate-700" },
  active: { label: "Aktiv", className: "bg-emerald-100 text-emerald-700" },
  on_hold: { label: "Pausiert", className: "bg-amber-100 text-amber-700" },
  completed: { label: "Abgeschlossen", className: "bg-sky-100 text-sky-700" },
  cancelled: { label: "Abgebrochen", className: "bg-rose-100 text-rose-700" },
};

const STATUS_OPTIONS = [
  { value: "planned", label: "Geplant" },
  { value: "active", label: "Aktiv" },
  { value: "on_hold", label: "Pausiert" },
  { value: "completed", label: "Abgeschlossen" },
  { value: "cancelled", label: "Abgebrochen" },
];

const CURRENCY_OPTIONS = [
  { value: "CHF", label: "CHF" },
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
];

const emptyForm = {
  name: "",
  code: "",
  status: "active",
  budget: "",
  currency: "CHF",
  start_date: "",
  end_date: "",
  hourly_rate: "",
  description: "",
};

export function Projects() {
  const { company } = useCompany();
  const companyId = company?._id;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [costsFor, setCostsFor] = useState(null);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/projects`);
      setProjects(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [companyId]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.code) {
      toast.error("Name + Code pflichtig.");
      return;
    }
    const payload = {
      name: form.name,
      code: form.code,
      status: form.status,
      currency: form.currency || "CHF",
      description: form.description || "",
      budget: form.budget ? parseFloat(form.budget) : null,
      hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    };
    try {
      if (editingId) {
        await apiClient.put(
          `/companies/${companyId}/projects/${editingId}`,
          payload
        );
        toast.success("Projekt aktualisiert.");
      } else {
        await apiClient.post(`/companies/${companyId}/projects`, payload);
        toast.success("Projekt angelegt.");
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen.");
    }
  };

  const edit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      code: p.code || "",
      status: p.status || "active",
      budget: p.budget ?? "",
      currency: p.currency || "CHF",
      start_date: p.start_date || "",
      end_date: p.end_date || "",
      hourly_rate: p.hourly_rate ?? "",
      description: p.description || "",
    });
    setShowForm(true);
  };

  const remove = async (p) => {
    if (!window.confirm(`Projekt "${p.name}" wirklich löschen?`)) return;
    try {
      await apiClient.delete(`/companies/${companyId}/projects/${p.id}`);
      toast.success("Projekt gelöscht.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const fmtMoney = (v, c) => {
    if (v == null) return "–";
    return `${(c || "CHF")} ${Number(v).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="p-6 space-y-5" data-testid="projects-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center">
            <Briefcase className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Projekte</h1>
            <p className="text-sm text-slate-500">
              Projekt-Management mit Stunden-Aggregation aus der Zeiterfassung.
            </p>
          </div>
        </div>
        <Button
          onClick={() => {
            if (showForm) resetForm();
            else setShowForm(true);
          }}
          data-testid="new-project-toggle-button"
        >
          {showForm ? <X className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          {showForm ? "Abbrechen" : "Neues Projekt"}
        </Button>
      </div>

      {showForm && (
        <Card className="p-4 border border-indigo-200 bg-indigo-50/40">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="project-form">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="project-name-input" />
            </div>
            <div>
              <Label className="text-xs">Code (intern, unique)</Label>
              <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} data-testid="project-code-input" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <SimpleSelect value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))} options={STATUS_OPTIONS} data-testid="project-status-select" />
            </div>
            <div>
              <Label className="text-xs">Währung</Label>
              <SimpleSelect value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))} options={CURRENCY_OPTIONS} />
            </div>
            <div>
              <Label className="text-xs">Budget</Label>
              <Input type="number" step="0.01" value={form.budget} onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))} data-testid="project-budget-input" />
            </div>
            <div>
              <Label className="text-xs">Stundensatz</Label>
              <Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setForm((f) => ({ ...f, hourly_rate: e.target.value }))} data-testid="project-rate-input" />
            </div>
            <div>
              <Label className="text-xs">Start</Label>
              <SwissDateField  value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Ende</Label>
              <SwissDateField  value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs">Beschreibung</Label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="md:col-span-3 flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={resetForm}>Abbrechen</Button>
              <Button type="submit" data-testid="project-submit-button">
                {editingId ? "Aktualisieren" : "Anlegen"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400" data-testid="projects-loading">
            <Loader2 className="h-5 w-5 inline animate-spin mr-2" /> Lade …
          </div>
        ) : projects.length === 0 ? (
          <div className="p-10 text-center text-slate-400" data-testid="projects-empty">
            Noch keine Projekte angelegt.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="projects-table">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Budget</th>
                <th className="text-right p-3">Kosten (ist)</th>
                <th className="text-right p-3">Stunden (ist)</th>
                <th className="text-right p-3">Umsatz (ist)</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => {
                const st = STATUS_LABELS[p.status] || STATUS_LABELS.active;
                const over = p.budget && p.actual_costs > p.budget;
                return (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => setCostsFor(p)} data-testid={`project-row-${p.id}`}>
                    <td className="p-3 font-mono text-xs">{p.code}</td>
                    <td className="p-3 font-medium">{p.name}</td>
                    <td className="p-3">
                      <Badge className={st.className}>{st.label}</Badge>
                    </td>
                    <td className="p-3 text-right">{fmtMoney(p.budget, p.currency)}</td>
                    <td className={`p-3 text-right ${over ? "text-rose-600 font-semibold" : ""}`} data-testid={`project-costs-${p.id}`} title={`${p.receipt_count || 0} Belege ${fmtMoney(p.receipt_costs, p.currency)} + Arbeitszeit`}>
                      {fmtMoney(p.actual_costs, p.currency)}
                      {p.receipt_count > 0 && <span className="ml-1 text-[10px] text-slate-400">({p.receipt_count} Belege)</span>}
                    </td>
                    <td className="p-3 text-right">{(p.actual_hours || 0).toFixed(2)} h</td>
                    <td className="p-3 text-right">{fmtMoney(p.actual_revenue, p.currency)}</td>
                    <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Button size="icon" variant="ghost" onClick={() => setCostsFor(p)} data-testid={`project-costs-open-${p.id}`} title="Belege & Kosten anzeigen">
                        <Receipt className="h-4 w-4 text-teal-700" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => edit(p)} data-testid={`project-edit-${p.id}`}>
                        <Pencil className="h-4 w-4 text-slate-500" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`project-delete-${p.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
      {costsFor && <ProjectCostsDialog companyId={companyId} project={costsFor} onClose={() => setCostsFor(null)} />}
    </div>
  );
}

export default Projects;
